import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchIcon, MapPinIcon, CalendarIcon, ShieldIcon, BuildingIcon, WifiIcon, ArrowRightIcon, SeatIcon } from "@/components/Icons";

const HERO_IMG = "https://images.unsplash.com/photo-1719159381981-1327b22aff9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxjb21wdXRlciUyMGxhYnxlbnwwfHx8fDE3ODUxODI2MzB8MA&ixlib=rb-4.1.0&q=85";

const EXAMS = ["SSC CGL", "SSC CHSL", "IBPS PO", "IBPS Clerk", "SBI PO", "RBI Grade B"];
const today = () => new Date().toISOString().slice(0, 10);

const UPCOMING_SLIDES = [
    { date: "16th August '26", time: "9 – 10 AM" },
    { date: "16th August '26", time: "11 – 12 PM" },
    { date: "16th August '26", time: "1 – 2 PM" },
];

export default function Landing() {
    const [city, setCity] = useState("");
    const [exam, setExam] = useState("");
    const [date, setDate] = useState("");
    const [centers, setCenters] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [slide, setSlide] = useState(0);
    const nav = useNavigate();

    useEffect(() => {
        const t = setInterval(() => {
            setSlide((s) => (s + 1) % UPCOMING_SLIDES.length);
        }, 3500);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        api.get("/centers").then((r) => setCenters(r.data.slice(0, 6))).catch(() => {});
    }, []);

    useEffect(() => {
        setLoadingCities(true);
        setCity("");
        api.get("/meta/cities", { params: exam ? { exam } : {} })
            .then((r) => setCities(r.data.cities || []))
            .catch(() => setCities([]))
            .finally(() => setLoadingCities(false));
    }, [exam]);

    const handleSearch = (e) => {
        e?.preventDefault();
        const p = new URLSearchParams();
        if (city) p.set("city", city);
        if (exam) p.set("exam", exam);
        if (date) p.set("date", date);
        nav(`/centers?${p.toString()}`);
    };

    return (
        <div>
            {/* HERO */}
            <section className="relative bg-[#0A0B10] text-white overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(0.4)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B10]/70 via-[#0A0B10]/60 to-[#0A0B10]" />
                <div className="absolute inset-0 bg-grid opacity-40" />
                <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-32">
                    <div className="max-w-3xl">
                        <div className="overline text-white/60 mb-6" data-testid="hero-eyebrow">SSC · Banking · Government Exams</div>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
                            Practice like the <span className="text-[#FFB800]">real thing.</span>
                        </h1>
                        <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
                            Book a real desk, a real timer, and real exam-hall silence at online test centers near you.
                            The way your final exam actually feels.
                        </p>
                    </div>

                    {/* SEARCH BAR */}
                    <form onSubmit={handleSearch} className="relative mt-12 grid grid-cols-1 md:grid-cols-12 gap-0 bg-white text-foreground border border-white/10 shadow-2xl">
                        <div className="md:col-span-4 flex items-center gap-3 px-5 py-4 border-b md:border-b-0 md:border-r border-border">
                            <BuildingIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="overline text-[10px]">Exam</div>
                                <Select value={exam} onValueChange={setExam}>
                                    <SelectTrigger className="border-0 p-0 h-6 shadow-none focus:ring-0 [&>svg]:ml-auto" data-testid="hero-exam">
                                        <SelectValue placeholder="Any exam" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EXAMS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="md:col-span-3 flex items-center gap-3 px-5 py-4 border-b md:border-b-0 md:border-r border-border">
                            <MapPinIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="overline text-[10px]">City</div>
                                <Select value={city} onValueChange={setCity} disabled={loadingCities}>
                                    <SelectTrigger className="border-0 p-0 h-6 shadow-none focus:ring-0 [&>svg]:ml-auto" data-testid="hero-city">
                                        <SelectValue placeholder={
                                            loadingCities ? "Loading…" :
                                            cities.length === 0 ? (exam ? `No cities for ${exam}` : "No cities yet") :
                                            "Pick a city"
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cities.map((c) => (
                                            <SelectItem key={c} value={c} data-testid={`hero-city-opt-${c}`}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="md:col-span-4 flex items-center gap-3 px-5 py-4 border-b md:border-b-0 md:border-r border-border">
                            <CalendarIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="overline text-[10px]">When</div>
                                <Input
                                    type="date"
                                    value={date}
                                    min={today()}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="border-0 shadow-none focus-visible:ring-0 px-0 h-6 text-sm font-medium"
                                    data-testid="hero-date"
                                />
                            </div>
                        </div>
                        <button
                            data-testid="hero-search-btn"
                            type="submit"
                            className="md:col-span-1 bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center px-5 py-4 gap-2 transition-colors duration-150"
                        >
                            <SearchIcon className="h-5 w-5" />
                            <span className="md:hidden font-medium">Search</span>
                        </button>
                    </form>

                    {/* UPCOMING TEST CAROUSEL */}
                    <div
                        className="relative mt-6 overflow-hidden border border-white/10 bg-white/5 backdrop-blur"
                        data-testid="upcoming-test-carousel"
                    >
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${slide * 100}%)` }}
                        >
                            {UPCOMING_SLIDES.map((s, i) => (
                                <div
                                    key={i}
                                    className="w-full shrink-0 px-6 py-6 sm:px-8 sm:py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                                >
                                    <div>
                                        <div className="overline text-[#FFB800] mb-1">Upcoming Test</div>
                                        <div className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">
                                            {s.date} <span className="text-white/40 mx-1">·</span> {s.time}
                                        </div>
                                    </div>
                                    <p className="text-xs sm:text-sm text-white/60">
                                        Register fast — seats are limited.
                                    </p>
                                </div>
                            ))}
                        </div>
                        <div className="absolute bottom-2 right-4 flex gap-1.5">
                            {UPCOMING_SLIDES.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSlide(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        i === slide ? "w-5 bg-[#FFB800]" : "w-1.5 bg-white/30"
                                    }`}
                                    data-testid={`carousel-dot-${i}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap gap-2">
                        {EXAMS.map((e) => (
                            <button
                                key={e}
                                onClick={() => { setExam(e); }}
                                className="px-3 py-1.5 border border-white/20 text-xs text-white/80 hover:border-[#FFB800] hover:text-[#FFB800] transition-colors duration-150 rounded-sm"
                                data-testid={`chip-${e.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {e}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* VALUE PROPS */}
            <section className="mx-auto max-w-7xl px-6 py-24">
                <div className="grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-4">
                        <div className="overline mb-4">Why BookedSlot</div>
                        <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight">
                            Your prep only counts if the <em className="not-italic text-primary">environment</em> is real.
                        </h2>
                    </div>
                    <div className="md:col-span-8 grid sm:grid-cols-3 gap-6">
                        {[
                            { icon: SeatIcon, title: "Real Desks", body: "Fixed seating, monitors, keyboards. No bed, no couch, no distractions." },
                            { icon: ShieldIcon, title: "Real Rules", body: "Photo ID check, invigilator watching, silence enforced. Just like exam day." },
                            { icon: WifiIcon, title: "Real Hardware", body: "Wired connection, standard monitors. Simulates the exact center machines." },
                        ].map(({ icon: I, title, body }) => (
                            <div key={title} className="border border-border p-6 hover:border-foreground hover:-translate-y-1 transition-all duration-150 bg-card" data-testid={`prop-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                                <I className="h-8 w-8 text-primary mb-6" />
                                <div className="font-heading text-xl font-bold">{title}</div>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED CENTERS */}
            <section className="border-t border-border bg-secondary/40">
                <div className="mx-auto max-w-7xl px-6 py-24">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="overline mb-3">Featured Test Centers</div>
                            <h2 className="font-heading text-4xl font-bold tracking-tight">Book a seat. Beat your best.</h2>
                        </div>
                        <Link to="/centers" className="hidden md:inline-flex items-center gap-2 text-sm hover:text-primary" data-testid="see-all-centers">
                            See all <ArrowRightIcon className="h-4 w-4" />
                        </Link>
                    </div>

                    {centers.length === 0 ? (
                        <div className="border border-dashed border-border p-10 text-center text-muted-foreground bg-card">
                            No centers yet. Center owners — <Link to="/register" className="text-primary underline">list your center</Link>.
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {centers.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/centers/${c.id}`}
                                    className="group border border-border bg-card hover:-translate-y-1 hover:shadow-md hover:border-foreground transition-all duration-150"
                                    data-testid={`center-card-${c.id}`}
                                >
                                    <div className="aspect-[16/9] bg-muted overflow-hidden">
                                        <img
                                            src={c.photo_urls?.[0] || "https://images.unsplash.com/photo-1574958269340-fa927503f3dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTE4MjY0MXww&ixlib=rb-4.1.0&q=85"}
                                            alt={c.name}
                                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                                        />
                                    </div>
                                    <div className="p-5">
                                        <div className="overline text-[10px]">{c.city}</div>
                                        <div className="font-heading text-lg font-bold mt-1">{c.name}</div>
                                        <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{c.address}</div>
                                        <div className="mt-4 flex items-center justify-between text-xs">
                                            <span className="font-mono text-muted-foreground">{c.total_seats} seats</span>
                                            <span className="text-primary group-hover:translate-x-1 transition-transform duration-150 inline-flex items-center gap-1">
                                                View slots <ArrowRightIcon className="h-3.5 w-3.5" />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0A0B10] text-white">
                <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="overline text-white/60 mb-4">For center owners</div>
                        <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight">Own a computer lab? Turn empty desks into revenue.</h2>
                        <p className="mt-4 text-white/70 leading-relaxed">List your seats & timings, we handle bookings, payments and admit-card issuance. You keep the lights on and the desks full.</p>
                        <Link to="/register?role=owner" data-testid="owner-cta">
                            <Button className="mt-8 rounded-sm bg-[#FFB800] text-[#0A0B10] hover:bg-[#FFB800]/90">
                                List your center <ArrowRightIcon className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                    <div className="border border-white/10 bg-white/5 p-8 backdrop-blur">
                        <div className="grid grid-cols-3 gap-6 text-center">
                            <div><div className="font-heading text-3xl font-bold">6+</div><div className="overline text-white/50 mt-2">Cities</div></div>
                            <div><div className="font-heading text-3xl font-bold">600+</div><div className="overline text-white/50 mt-2">Desks</div></div>
                            <div><div className="font-heading text-3xl font-bold">1200+</div><div className="overline text-white/50 mt-2">Aspirants</div></div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-border">
                <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col md:flex-row md:justify-between gap-4 text-sm text-muted-foreground">
                    <div>© {new Date().getFullYear()} BookedSlot. Practice like the real thing.</div>
                    <div className="flex items-center gap-6">
                        <Link to="/contact" className="hover:text-primary" data-testid="footer-contact-link">
                            Contact Us
                        </Link>
                        <div className="font-mono">SSC · IBPS · SBI · RBI · UPSC</div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
